import { BOARD_AI_LITERACY_QUESTIONS } from './board-ai-literacy.questions';

export interface Recommendation {
  questionId: string;
  prompt: string;
  action: string;
}

const ACTION_BY_QUESTION: Record<string, string> = {
  q1: 'Draft an AI charter: a 1-page board document naming where AI fits, where it deliberately does not, and the AI lead. This is the single highest-leverage piece because everything else descends from it.',
  q2: 'Formally name an executive AI lead with KPIs and a quarterly board update commitment. Without ownership, governance becomes everyone’s and no-one’s problem.',
  q3: 'Adopt an AI acceptable-use policy (calibrated to ISO/IEC 42001 and the Voluntary AI Safety Standard). Get it board-approved within 30 days.',
  q4: 'Run a shadow-AI inventory: a 2-week sweep of every AI tool, sanctioned and unsanctioned, in use across the business. It is the most common reason boards under-estimate their exposure.',
  q5: 'Build an AI-specific risk register with named owners and a quarterly review cadence. It does not need to be sophisticated; it needs to exist.',
  q6: 'Identify your top three AI vendors by data exposure and run a formal Privacy Act + data-residency assessment on each. These are usually the biggest unmanaged risks.',
  q7: 'Extend your incident response plan with explicit AI scenarios: model misuse, data leak via AI tool, vendor-side breach. Test it with a tabletop exercise.',
  q8: 'Designate a regulatory horizon scan owner. The Voluntary AI Safety Standard, ISO/IEC 42001, and sector regulator guidance are moving fast, and falling behind is a board-level risk.',
  q9: 'Commission a structured board AI literacy programme. An afternoon with a competent advisor is more efficient than self-study reading.',
  q10: 'Make literacy concrete: directors should be able to define generative vs predictive vs agentic AI in one sentence each. This is a 20-minute fix, not a course.',
  q11: 'Build director confidence to challenge by introducing structured AI decision papers (problem → option → risk → recommendation) so every director has the same scaffold.',
  q12: 'Adopt a quarterly board AI report covering inventory, value capture, and incidents. The discipline of producing it surfaces things that verbal updates miss.',
  q13: 'Add technology executive experience to your board skills matrix and recruit accordingly. Even a non-exec with adjacent tech leadership experience moves the needle.',
  q14: 'Agree a board-level AI use-case rubric (data sensitivity, vendor risk, business impact). Without it, every approval becomes ad hoc and inconsistent.',
};

export function topThreeRecommendations(answers: Record<string, string>): Recommendation[] {
  const ranked: Array<{ q: typeof BOARD_AI_LITERACY_QUESTIONS[number]; weight: number }> = [];
  for (const q of BOARD_AI_LITERACY_QUESTIONS) {
    const a = answers[q.id];
    if (!a || a === 'skip') continue;
    const opt = q.options.find((o) => o.id === a);
    if (!opt) continue;
    ranked.push({ q, weight: opt.weight });
  }
  // Only surface genuine gaps — questions answered below the strongest option
  // (weight 4). A board that scored top marks everywhere gets no "priorities"
  // list; the result page shows a maintain message instead.
  const gaps = ranked.filter((r) => r.weight < 4).sort((a, b) => a.weight - b.weight);
  return gaps.slice(0, 3).map(({ q }) => ({
    questionId: q.id,
    prompt: q.prompt,
    action: ACTION_BY_QUESTION[q.id] ?? '',
  }));
}
